import React, { useState } from 'react';
import { Modal } from '../../components/Modal';
import { ModalFooter } from '../../components/ModalFooter';
import { useCreateInvitation, useInvitations } from '../../hooks/useInvitations';
import { useProjectUsers } from '../../hooks/useProjectUsers';
import { useProjectRoles } from '../../hooks/useProjectRoles';

interface Props {
  projectId: string;
  projectName: string;
  onClose: () => void;
}

export function InviteUserModal({ projectId, projectName, onClose }: Props) {
  const { data: roles = [] } = useProjectRoles(projectId);
  const defaultRoleId = roles.find(r => r.name === 'ROLE_USER')?.id ?? roles[0]?.id ?? '';
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [dirty, setDirty] = useState(false);
  const create = useCreateInvitation(projectId);
  const { data: existingUsers = [] } = useProjectUsers(projectId);
  const { data: existingInvitations = [] } = useInvitations(projectId);

  // Default to ROLE_USER once roles load
  const effectiveRoleId = roleId || defaultRoleId;

  const normalizedEmail = email.trim().toLowerCase();
  const emailExists =
    existingUsers.some(u => u.email.toLowerCase() === normalizedEmail) ||
    existingInvitations.some(i => i.email.toLowerCase() === normalizedEmail);

  const emailError =
    dirty && !email.trim() ? 'Email is required' :
    dirty && email.length > 255 ? 'Email must be less than 255 characters' :
    dirty && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? 'Email is invalid' :
    dirty && emailExists ? 'This email has already been invited or is already a member' :
    undefined;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setDirty(true);
    if (emailError || emailExists || !effectiveRoleId) return;
    const roleName = roles.find(r => r.id === effectiveRoleId)?.name ?? '';
    create.mutate(
      { email: email.trim(), role: effectiveRoleId, roleName },
      { onSuccess: onClose }
    );
  };

  return (
    <Modal
      title="Invite User"
      icon="fas fa-user"
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <ModalFooter
          onClose={onClose}
          nextLabel="Save"
          nextDisabled={!email.trim() || emailExists}
          saving={create.isPending}
          savingLabel="Saving..."
        />
      }
    >
      <div className="form-group">
        <h4>Project: <small><i className="fas fa-gavel" aria-hidden="true" /> {projectName}</small></h4>
      </div>

      <div className={`form-group${emailError ? ' has-error' : ''}`}>
        {emailError && <span className="help-block">{emailError}</span>}
        <input
          type="email"
          placeholder="Email Address"
          className="form-control"
          value={email}
          onChange={e => { setEmail(e.target.value); setDirty(true); }}
          maxLength={255}
          disabled={create.isPending}
          autoFocus
        />
      </div>

      <div className="form-group">
        <div className="btn-group">
          {roles.map(r => (
            <button
              key={r.id}
              type="button"
              className={`btn btn-default${effectiveRoleId === r.id ? ' active' : ''}`}
              onClick={() => setRoleId(r.id)}
              disabled={create.isPending}
            >
              {r.name}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
